import { useState, useEffect } from 'react';
import { Box, Flex, Text, Heading, Spinner, Button, Table, Dialog } from '@radix-ui/themes';
import { Download } from 'lucide-react';
import { apiFetch } from './api';

import { formatUGX } from "./UtilityFunctions";
import { CancelIcon, ICON_SIZE } from "./actionIcons";
/**
 * One invoice, openable from Settings → Invoices & Credits, and downloadable as a PDF.
 *
 * A dealership needs to be able to produce this for their own bookkeeping
 * without asking us for a copy, and the emailed version cannot be relied on:
 * it goes to whatever address is on file, and some tenants have none.
 */

//Shared formatter — see formatUGX in UtilityFunctions.jsx. This rendered
//"1,500,000 UGX" (suffixed) while most of the app rendered "UGX 1,500,000".
const ugx = (n) => formatUGX(n || 0);

const day = (iso) => (iso
  ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  : '—');

const STATUS = {
  paid: { label: 'Paid', color: 'green' },
  issued: { label: 'Due', color: 'amber' },
  written_off: { label: 'Written off', color: 'gray' },
  open: { label: 'Open', color: 'gray' },
};

export default function InvoiceDialog({ invoiceId, companyName, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;
    setData(null);
    setError('');
    apiFetch(`/billing/invoices/${invoiceId}`, { method: 'GET' })
      .then((r) => r.json())
      .then((body) => {
        if (cancelled) return;
        if (body.success === false) throw new Error(body.error || 'Could not load the invoice');
        setData(body);
      })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load the invoice'); });
    return () => { cancelled = true; };
  }, [invoiceId]);

  const download = async () => {
    if (!data) return;
    setDownloading(true);
    try {
      // Dynamic import, matching ReportGenerator: jsPDF is ~350KB and only
      // matters to someone who actually asks for a copy, so it stays out of the
      // entry chunk.
      const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable'),
      ]);
      const { invoice, lines } = data;
      const pdf = new jsPDF('p', 'mm', 'a4');
      const w = pdf.internal.pageSize.getWidth();

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.text('INVOICE', 15, 22);
      pdf.setFontSize(11);
      pdf.text(invoice.InvoiceNo || '', w - 15, 22, { align: 'right' });

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(90);
      pdf.text('Auto Dealer UG', 15, 32);
      pdf.text(`Billed to: ${companyName || ''}`, 15, 38);
      pdf.text(`Period: ${invoice.Period}`, w - 15, 32, { align: 'right' });
      pdf.text(`Issued: ${day(invoice.IssuedAt)}`, w - 15, 38, { align: 'right' });
      pdf.text(`Due: ${day(invoice.DueAt)}`, w - 15, 44, { align: 'right' });

      autoTable(pdf, {
        startY: 54,
        head: [['Description', 'Qty', 'Amount (UGX)']],
        body: lines.map((l) => [
          l.Description,
          l.Kind === 'subscription' || l.Kind === 'adjustment' ? '' : Number(l.Quantity).toLocaleString(),
          formatUGX(l.AmountUGX, { symbol: false }),
        ]),
        foot: [['', 'Total due', formatUGX(invoice.TotalUGX, { symbol: false })]],
        theme: 'plain',
        headStyles: { fontStyle: 'bold', lineWidth: { bottom: 0.2 }, lineColor: 200 },
        footStyles: { fontStyle: 'bold', lineWidth: { top: 0.2 }, lineColor: 200 },
        columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
        styles: { fontSize: 10, cellPadding: 2 },
      });

      let y = pdf.lastAutoTable.finalY + 12;
      if (invoice.Status === 'paid') {
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(20, 120, 60);
        pdf.text(`PAID — ${day(invoice.PaidAt)}`, 15, y);
        if (invoice.PaymentNote) {
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(90);
          pdf.text(invoice.PaymentNote, 15, y + 6);
        }
        y += 14;
      }

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(130);
      pdf.text(
        'Credit purchases and the subscription line both cover the period shown above.',
        15, y
      );

      pdf.save(`${invoice.InvoiceNo || 'invoice'}.pdf`);
    } catch (e) {
      setError('Could not generate the PDF.');
    } finally {
      setDownloading(false);
    }
  };

  const invoice = data?.invoice;
  const status = invoice ? (STATUS[invoice.Status] || STATUS.open) : null;

  return (
    <Dialog.Root open={!!invoiceId} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Content style={{ maxWidth: 560 }} id="invoice-dialog">
        <Flex justify="between" align="start" mb="1">
          <Dialog.Title>{invoice?.InvoiceNo || 'Invoice'}</Dialog.Title>
          {status && <Text size="2" color={status.color} weight="medium">{status.label}</Text>}
        </Flex>

        {!data && !error && <Flex justify="center" py="6"><Spinner size="2" /></Flex>}
        {error && <Text color="red" size="2">{error}</Text>}

        {invoice && (
          <>
            <Text as="p" size="2" color="gray" mb="4">
              {companyName ? `${companyName} · ` : ''}Period {invoice.Period}
              {invoice.IssuedAt ? ` · issued ${day(invoice.IssuedAt)}` : ''}
              {invoice.DueAt ? ` · due ${day(invoice.DueAt)}` : ''}
            </Text>

            <Table.Root variant="surface" size="1">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Description</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell justify="end">Amount</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data.lines.map((l) => (
                  <Table.Row key={l.ID}>
                    <Table.Cell>{l.Description}</Table.Cell>
                    <Table.Cell justify="end">{ugx(l.AmountUGX)}</Table.Cell>
                  </Table.Row>
                ))}
                <Table.Row>
                  <Table.Cell><Text weight="bold">Total due</Text></Table.Cell>
                  <Table.Cell justify="end"><Text weight="bold">{ugx(invoice.TotalUGX)}</Text></Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table.Root>

            {invoice.Status === 'paid' && (
              <Text as="p" size="2" color="green" mt="3">
                Paid {day(invoice.PaidAt)}{invoice.PaymentNote ? ` · ${invoice.PaymentNote}` : ''}
              </Text>
            )}

            <Flex gap="3" justify="end" mt="4">
              <Button variant="soft" color="gray" onClick={onClose}><CancelIcon size={ICON_SIZE} />Close</Button>
              <Button onClick={download} disabled={downloading}>
                <Download size={14} />
                {downloading ? 'Preparing…' : 'Download PDF'}
              </Button>
            </Flex>
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
